/*
We snapshot every 100 actions (to aid in getting a previous version of the
aggregate, which is used to undo) but don't store snapshots.

The only thing we store in the DB is the unconfirmed actions.

Actions for an aggregate must start at 0 and have no gaps. If we detect a gap,
that means that we missed a message from the server and need to download the
missing actions.
*/

import type {
  ActionMessage,
  Reducer,
  UnconfirmedActionMessage,
} from "@engine/core";
import type { ActionError, ClientStorageAdapter } from "@engine/network";
import type { RoundAggregate } from "@engine/round/aggregate";
import Dexie, { type EntityTable } from "dexie";
import { NumberOrRange } from "server/routes/getAggregateActionsByVersion";
import { model as modelVersion } from "../../version";

type Db = Dexie & {
  metadata: EntityTable<{
    id: "singleton";
    userProfileHash: string;
    modelVersion: number;
  }>;
  unconfirmedActions2: EntityTable<{
    aggregateId: string;
    actionId: string;
    actionMessage: UnconfirmedActionMessage;
    createdAt: number;
  }>;
};

function getDb(): Db {
  const db = new Dexie("SPAStorage") as Db;

  db.version(4).stores({
    metadata: "[id]",
    unconfirmedActions2:
      "[aggregateId+actionId], [aggregateId+version], [aggregateId]",

    // Old tables which we want Dexie to delete https://github.com/dexie/Dexie.js/issues/275
    unconfirmedActions: null,
    confirmedActions: null,
  });

  return db;
}

export class SPAStorageAdapter implements ClientStorageAdapter {
  aggregateId: string;
  userProfileHash: string;

  currentUnconfirmedAgg: RoundAggregate;
  lastConfirmedAgg: RoundAggregate;

  confirmedActionsSinceLoad: ActionMessage[] = [];
  confirmedSnapshotsSinceLoad: RoundAggregate[] = [];

  db: Db;
  downloadActionsByVersion: (
    aggregateId: string,
    versions: NumberOrRange[],
  ) => Promise<{ [version: number]: ActionMessage }>;

  constructor(
    aggregateId: string,
    userProfileHash: string,
    lastConfirmedAgg: RoundAggregate,
    currentUnconfirmedAgg: RoundAggregate,
    db: Db,
    downloadSpecificActions: (
      aggregateId: string,
      versions: NumberOrRange[],
    ) => Promise<{ [version: number]: ActionMessage }>,
    snapshotsSinceLoad: RoundAggregate[],
  ) {
    this.aggregateId = aggregateId;
    this.userProfileHash = userProfileHash;
    this.currentUnconfirmedAgg = currentUnconfirmedAgg;
    this.lastConfirmedAgg = lastConfirmedAgg;
    this.db = db;
    this.downloadActionsByVersion = downloadSpecificActions;
    this.confirmedSnapshotsSinceLoad = snapshotsSinceLoad;
  }

  static async init(
    reducer: Reducer,
    aggregateId: string,
    userProfileHash: string,
    downloadRound: (aggregateId: string) => Promise<RoundAggregate>,
    downloadActionsById: (
      aggregateId: string,
      ids: string[],
    ) => Promise<{ [id: string]: ActionMessage }>,
    downloadActionsByVersion: (
      aggregateId: string,
      versions: NumberOrRange[],
    ) => Promise<{ [version: number]: ActionMessage }>,
  ): Promise<SPAStorageAdapter> {
    const db = getDb();

    const [updatedConfirmedAgg, unconfirmedActions] = await Promise.all([
      downloadRound(aggregateId),
      db.unconfirmedActions2.where("aggregateId").equals(aggregateId).toArray(),
      validateDb(db, userProfileHash),
    ]);

    let currentUnconfirmedAgg = updatedConfirmedAgg;
    if (unconfirmedActions.length > 0) {
      const confirmedActions = await downloadActionsById(
        aggregateId,
        unconfirmedActions.map((action) => action.actionId),
      );
      for (const action of unconfirmedActions) {
        const appliedAction = confirmedActions[action.actionId];
        if (appliedAction) {
          await db.unconfirmedActions2
            .where("aggregateId")
            .equals(aggregateId)
            .and((action) => action.actionId === action.actionId)
            .delete();
          continue;
        }

        const result = reducer.performAction(
          currentUnconfirmedAgg,
          action.actionMessage,
        );
        if (result instanceof Error) {
          throw result;
        }
        currentUnconfirmedAgg = result;
      }
    }

    return new SPAStorageAdapter(
      aggregateId,
      userProfileHash,
      updatedConfirmedAgg,
      currentUnconfirmedAgg,
      db,
      downloadActionsByVersion,
      [updatedConfirmedAgg],
    );
  }

  async performUnconfirmedAction(
    reducer: Reducer,
    actionMessage: UnconfirmedActionMessage,
  ): Promise<null | Error> {
    const updatedAggregateOrError = reducer.performAction(
      this.currentUnconfirmedAgg,
      actionMessage,
    );

    if (updatedAggregateOrError instanceof Error) {
      return updatedAggregateOrError;
    }

    this.currentUnconfirmedAgg = updatedAggregateOrError;

    await this.db.unconfirmedActions2.put({
      createdAt: Date.now(),
      aggregateId: this.aggregateId,
      actionId: actionMessage.id,
      actionMessage,
    });

    return null;
  }

  async getAction(actionId: string): Promise<UnconfirmedActionMessage | null> {
    const action = await this.db.unconfirmedActions2.get([
      this.aggregateId,
      actionId,
    ]);
    if (action) {
      return action.actionMessage;
    }

    for (const action of this.confirmedActionsSinceLoad) {
      if (action.id === actionId) {
        return action;
      }
    }

    return null;
  }

  async getAggregateAtVersion(
    reducer: Reducer,
    version: number,
  ): Promise<RoundAggregate | null> {
    let lastAggregatePriorToAction: RoundAggregate | null = null;
    if (this.confirmedSnapshotsSinceLoad.length > 0) {
      for (let i = 0; i < this.confirmedSnapshotsSinceLoad.length; i++) {
        if (this.confirmedSnapshotsSinceLoad[i].version <= version) {
          lastAggregatePriorToAction = this.confirmedSnapshotsSinceLoad[i];
        } else if (this.confirmedSnapshotsSinceLoad[i].version > version) {
          break;
        }
      }
    }

    const aggregateVersion = lastAggregatePriorToAction?.version ?? -1;
    if (aggregateVersion === -1) {
      return null;
    }

    let aggregate = lastAggregatePriorToAction;
    if (aggregateVersion < version) {
      const replayStart = this.confirmedActionsSinceLoad.findIndex(
        (action) => action.version === aggregateVersion + 1,
      );
      if (replayStart === -1) {
        throw new Error("Could not find replay start");
      }

      for (
        let i = replayStart;
        i < this.confirmedActionsSinceLoad.length;
        i++
      ) {
        const action = this.confirmedActionsSinceLoad[i];
        const result = reducer.performAction(aggregate, action);
        if (result instanceof Error) {
          throw result;
        }
        aggregate = result;
        if (action.version === version) break;
      }
    }

    return aggregate;
  }

  async performConfirmedAction(
    reducer: Reducer,
    actionMessage: ActionMessage,
  ): Promise<Error | ActionError[]> {
    if (actionMessage.version === this.lastConfirmedAgg.version) {
      return [];
    }

    // Need to download actions from lastConfirmedAgg.version + 1 to
    // actionMessage.version - 1 before proceeding
    if (actionMessage.version > this.lastConfirmedAgg.version + 1) {
      const missingActionNumbers = Array.from(
        { length: actionMessage.version - this.lastConfirmedAgg.version - 1 },
        (_, i) => this.lastConfirmedAgg.version + i + 1,
      );
      console.log("downloading missing actions", missingActionNumbers);
      const missingActions = await this.downloadActionsByVersion(
        this.aggregateId,
        missingActionNumbers,
      );

      const missingFromMissingActions: number[] = [];
      for (const actionNumber of missingActionNumbers) {
        if (missingActions[actionNumber] === undefined) {
          missingFromMissingActions.push(actionNumber);
        }
      }

      if (missingFromMissingActions.length > 0) {
        console.log("missingActionNumbers", missingActionNumbers);
        console.log("missingActions", missingActions);
        console.log("missingFromMissingActions", missingFromMissingActions);
        throw new Error(
          `Missing actions for aggregate ${this.aggregateId}: ${missingFromMissingActions.join(
            ", ",
          )}`,
        );
      }

      for (const actionNumber of missingActionNumbers) {
        await this.performConfirmedAction(
          reducer,
          missingActions[actionNumber],
        );
      }
    }

    const result = reducer.performAction(this.lastConfirmedAgg, actionMessage);
    if (result instanceof Error) {
      return result;
    }

    this.lastConfirmedAgg = result;
    this.confirmedActionsSinceLoad.push(actionMessage);
    if (result.version % 100 === 0) {
      this.confirmedSnapshotsSinceLoad.push(result);
    }

    const unconfirmedActions = (
      await this.db.unconfirmedActions2
        .where("aggregateId")
        .equals(this.aggregateId)
        .toArray()
    ).map((action) => action.actionMessage);

    let errors: ActionError[] = [];
    let currentUnconfirmedAgg: RoundAggregate = this.lastConfirmedAgg;
    for (const unconfirmedAction of unconfirmedActions) {
      if (unconfirmedAction.id === actionMessage.id) {
        continue;
      }
      const result = reducer.performAction(
        currentUnconfirmedAgg,
        unconfirmedAction,
      );
      if (result instanceof Error) {
        errors.push({ actionId: unconfirmedAction.id, error: result });
      } else {
        currentUnconfirmedAgg = result;
      }
    }

    this.currentUnconfirmedAgg = currentUnconfirmedAgg;

    await this.db.unconfirmedActions2
      .where("[aggregateId+actionId]")
      .equals([this.aggregateId, actionMessage.id])
      .delete();

    return errors;
  }

  // Check:
  // 1. The version of the last confirmed action (N_a) is less than or equal to
  //    the last confirmed aggregate (N_d): N_a <= N_d
  // 2. getUnconfirmedAggregateSync().version (N_u): N_u >= N_a
  // 3. No unconfirmed actions with the same id as a confirmed action
  async checkConsistency(): Promise<void> {
    const confirmedActionsSinceLoad = this.confirmedActionsSinceLoad;
    const lastConfirmedAction =
      confirmedActionsSinceLoad.length > 0
        ? confirmedActionsSinceLoad[confirmedActionsSinceLoad.length - 1]
        : null;

    const unconfirmedActions = await this.db.unconfirmedActions2
      .where("aggregateId")
      .equals(this.aggregateId)
      .toArray();

    const n_a = lastConfirmedAction?.version ?? -1;
    const n_d = this.lastConfirmedAgg.version;

    if (n_a > n_d) {
      throw new Error(
        `lastConfirmedAction.version (${n_a}) > this.lastConfirmedAgg.version (${n_d})`,
      );
    }

    if (this.getUnconfirmedAggregateSync().version < n_a) {
      throw new Error(
        `getUnconfirmedAggregateSync().version (${this.getUnconfirmedAggregateSync().version}) < n_a (${n_a})`,
      );
    }

    const confirmedActionIds = new Set(
      confirmedActionsSinceLoad.map((action) => action.id),
    );
    for (const unconfirmedAction of unconfirmedActions) {
      if (confirmedActionIds.has(unconfirmedAction.actionId)) {
        throw new Error(
          `unconfirmed action ${unconfirmedAction.actionId} has the same id as a confirmed action`,
        );
      }
    }
  }

  async clearUnconfirmedActions(): Promise<void> {
    await this.db.unconfirmedActions2
      .where("aggregateId")
      .equals(this.aggregateId)
      .delete();
    this.currentUnconfirmedAgg = this.lastConfirmedAgg;
  }

  getConfirmedActions(): ActionMessage[] {
    return this.confirmedActionsSinceLoad;
  }

  async getUnconfirmedActions(): Promise<
    {
      createdAt: number;
      message: UnconfirmedActionMessage;
    }[]
  > {
    return (
      await this.db.unconfirmedActions2
        .where("aggregateId")
        .equals(this.aggregateId)
        .sortBy("createdAt")
    ).map(({ actionMessage, createdAt }) => ({
      message: actionMessage,
      createdAt,
    }));
  }

  getUnconfirmedAggregateSync(): RoundAggregate {
    return this.currentUnconfirmedAgg;
  }

  getConfirmedAggregateSync(): RoundAggregate {
    return this.lastConfirmedAgg;
  }

  getLastConfirmedVersion(): number {
    return this.lastConfirmedAgg.version;
  }

  async getAggregateBeforeUnconfirmedAction(
    reducer: Reducer,
    actionId: string,
  ): Promise<RoundAggregate | Error> {
    const actions = await this.getUnconfirmedActions();
    let agg = this.lastConfirmedAgg;
    for (const action of actions) {
      if (action.message.id === actionId) {
        return agg;
      }
      const result = reducer.performAction(agg, action.message);
      if (result instanceof Error) {
        return result;
      }
      agg = result;
    }
    return agg;
  }
}

async function validateDb(db: Db, userProfileHash: string): Promise<void> {
  const metadata = await db.metadata.get(["singleton"]);
  await db.metadata.put({ id: "singleton", userProfileHash, modelVersion });
  let mustClearDb = false;

  // Note that a UserProfile includes the track, so this will clear the db if
  // the track changes
  if (
    metadata?.userProfileHash !== userProfileHash ||
    metadata?.modelVersion !== modelVersion
  ) {
    console.log(
      "Clearing database due to user profile hash or model version change",
    );
    mustClearDb = true;
  }

  if (mustClearDb) {
    await db.unconfirmedActions2.clear();
  }
}
